/**
 * AI CONTEXT
 * Purpose: tryout intake/progress table; routes to create or progress modal.
 * Key deps: useTryoutTableData, AddTryoutFormModal, TryoutProgressFormModal.
 * Referenced by: App route ROUTES.TRYOUTS.
 * Importance: student onboarding pipeline.
 * Refactor: replace nested ternary modal selection with explicit mode variable.
 */
import { useMemo, useState } from "react"

import CopyToClipboard from "#blocks/CopyToClipboard"
import EmptyPage from "#blocks/EmptyPage"
import Flex from "#blocks/Flex"
import Page from "#blocks/Page"
import Table2 from "#blocks/Table/Table2"
import TableFiltersRow from "#blocks/Table/TableFiltersRow"
import { TRYOUT_TABLE_COLUMN_CONFIG } from "#constants/tryoutTableColumns"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { type TryoutForTable } from "#customTypes/composedTypes/tryoutTypes"
import { SETTINGS_KEYS, type Uuid } from "#customTypes/globalTypes"
import AddTryoutFormModal from "#forms/AddTryout/AddTryoutFormModal"
import TryoutProgressFormModal from "#forms/TryoutProgressForm/TryoutProgressFormModal"
import useTryoutTableData from "#hooks/dataHooks/useTryoutTableData"
import useHasPermission from "#hooks/newHooks/useHasPermission"

import type { ColumnsType } from "antd/es/table"

const TryoutsTable = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedTryoutId, setSelectedTryoutId] = useState<Uuid | null>(null)

  const {
    tryoutsForTable,
    isReady,
    errors,
    onSearch,
    sortField,
    sortOrder,
    onSortChange,
  } = useTryoutTableData()

  const canSeeTryouts = useHasPermission(RESOURCES.TRYOUT, USER_ACTIONS.READ)
  const canViewId = useHasPermission(RESOURCES.ID, USER_ACTIONS.READ)

  // Tryouts use a hand-written column config; prepend the DEV-only id copy
  // column to match the generic buildTableColumns tables.
  const columns = useMemo<ColumnsType<TryoutForTable>>(() => {
    if (!canViewId) return TRYOUT_TABLE_COLUMN_CONFIG
    return [
      {
        title: "ID",
        dataIndex: "id",
        key: "id",
        width: 50,
        align: "center",
        render: (value: string) => <CopyToClipboard data={value} compact />,
      },
      ...TRYOUT_TABLE_COLUMN_CONFIG,
    ]
  }, [canViewId])

  const handleOpen = () => {
    if (!canSeeTryouts) {
      return
    }

    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
    setSelectedTryoutId(null)
  }

  const handleRowClick = (selectedTryoutId: string) => {
    setSelectedTryoutId(selectedTryoutId)
    handleOpen()
  }

  if (!canSeeTryouts) {
    return (
      <Page>
        <EmptyPage />
      </Page>
    )
  }

  return (
    <Page>
      <Flex col style={{ padding: 20, width: "100%", maxWidth: 2200 }} gap={12}>
        <h1>Tryouts</h1>

        <TableFiltersRow
          onAdd={handleOpen}
          onSearch={onSearch}
          searchPlaceholder="Search name, class name, start date"
          settingsKey={SETTINGS_KEYS.TRYOUTS}
        />

        {/* We use a different form once the tryout is created */}
        {isOpen ? (
          selectedTryoutId ? (
            <TryoutProgressFormModal
              onClose={handleClose}
              tryoutId={selectedTryoutId}
            />
          ) : (
            <AddTryoutFormModal
              onClose={handleClose}
              tryoutId={selectedTryoutId || undefined}
            />
          )
        ) : null}

        <Table2<TryoutForTable>
          data={tryoutsForTable}
          columns={columns}
          onRowClick={handleRowClick}
          isLoading={!isReady}
          errors={errors}
          sortField={sortField}
          sortOrder={sortOrder}
          onSortChange={onSortChange}
        />
      </Flex>
    </Page>
  )
}

export default TryoutsTable
